import { t } from "@lingui/macro";
import { useIsFocused } from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useRef } from "react";
import { Animated, Platform, StatusBar } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { BookNow } from "@/features/BookNow";
import { PackageNeedsAttentionBanner } from "@/features/Packages/PackageNeedsAttentionBanner";
import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container";
import { Box } from "@whoosh/design-system/src/components/primitive";

import useMetaThemeColor from "../../../hooks/useMetaThemeColor";
import { HomeInternalQuery } from "../components/__generated__/HomeInternalQuery.graphql";
import { BookToday } from "./BookToday";
import { CustomerSchedule } from "./CustomerSchedule";
import { FeaturedEvents } from "./FeaturedEvents/FeaturedEvents";
import { MemberSchedule } from "./MemberSchedule";
import { MobileHeader } from "./MobileHeader";
import { SideNavBar } from "./SideNavBar/SideNavBar";

type HomeInternalProps = {
  queryRef: PreloadedQuery<HomeInternalQuery>;
};

export const HomeInternal = ({ queryRef }: HomeInternalProps) => {
  const calendarOrientation = useResponsiveProp({ base: "vertical" });

  const twoMonthView = useResponsiveProp({ base: 0, md: 1 }) === 1;

  const data = usePreloadedQuery(HomeInternalQueryString, queryRef);

  const routeIsFocused = useIsFocused();
  useMetaThemeColor(routeIsFocused, "#001A19");

  const numBookings =
    ((data.session?.currentClientProfile?.member?.bookings?.count ?? 0) || 0) +
    (data.session?.currentClientProfile?.member?.managedBookings?.count ?? 0) +
    (data.session?.currentClientProfile?.member?.eventSignUps?.count ?? 0);

  const club = data.session?.currentClientProfile?.member?.club ?? null;

  const memberProfileClass =
    data.session?.currentClientProfile?.member?.clientClass ?? "CUSTOMER";

  const scrollY = useRef(new Animated.Value(0)).current;

  const inputRange = [0, 200];
  const opacity = scrollY.interpolate({
    inputRange,
    outputRange: [0, 1],
  });

  StatusBar.setBarStyle("dark-content");

  return (
    <Box flex={1} bg="surface-primary">
      {Platform.OS !== "web" ? (
        <Animated.View
          style={{
            height: StatusBar.currentHeight || 44,
            width: "100%",
            backgroundColor: "white",
            position: "absolute",
            opacity: opacity,
            zIndex: 100000,
            top: 0,
          }}
        />
      ) : null}
      <Animated.ScrollView
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: Platform.OS !== "web" }
        )}
        bounces={false}
        contentContainerStyle={{
          width: "100%",
          marginHorizontal: "auto",
          position: "relative",
          paddingBottom: 100,
        }}
        showsVerticalScrollIndicator={Platform.OS === "web"}
        scrollEventThrottle={5}
      >
        {club ? (
          <MobileHeader
            customBG={club?.appCustomization?.backgroundImage?.publicId}
            clubRef={club}
          />
        ) : null}

        <Container
          paddingTop={{ base: 0, lg: 6 }}
          paddingHorizontal={{ base: 0, lg: 16 }}
          flexDirection="row"
        >
          {data.session ? (
            <SideNavBar sessionRef={data.session ?? null} />
          ) : null}

          <Box flex={1}>
            {memberProfileClass === "CUSTOMER" ? (
              <Box px={{ base: 6, md: 16, lg: 12 }}>
                <PackageNeedsAttentionBanner
                  customerRef={data.session?.currentClientProfile?.member}
                />
              </Box>
            ) : null}
            {numBookings > 0 ? (
              memberProfileClass === "CUSTOMER" ? (
                <CustomerSchedule
                  customerBookingsRef={
                    data.session?.currentClientProfile?.member ?? null
                  }
                />
              ) : (
                <MemberSchedule
                  memberBookingsRef={
                    data.session?.currentClientProfile?.member ?? null
                  }
                  memberEventSignUpsRef={
                    data.session?.currentClientProfile?.member ?? null
                  }
                />
              )
            ) : (
              <Container
                paddingHorizontal={{
                  base: 0,
                  lg: 12,
                }}
              >
                <BookToday bookTodayRef={club} />
              </Container>
            )}
            {club ? <FeaturedEvents clubRef={club} /> : null}

            <Box paddingHorizontal={{ base: 6, md: 16, lg: 12 }}>
              <BookNow
                sessionRef={data.session ?? null}
                title={t`Book Now`}
                calendarOrientation={calendarOrientation}
                twoMonthView={twoMonthView}
                syncSelectedOptionWithStoredDefault={routeIsFocused}
              />
            </Box>
          </Box>
        </Container>
      </Animated.ScrollView>
    </Box>
  );
};

export const HomeInternalQueryString = graphql`
  query HomeInternalQuery(
    $dateGTE: Date!
    $dateTimeGTE: Datetime!
    $memberId: ID!
  ) {
    session {
      ...BookNow_session
      ...SideNavBarFragment_session
      currentClientProfile {
        member {
          clientClass
          club {
            ...MobileHeaderFragment_club
            ...BookTodayFragment_Facilities
            ...FeaturedEventsFragment_club @arguments(dateGTE: $dateGTE)
            appCustomization {
              backgroundImage {
                # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                publicId: renderingKey
              }
            }
          }
          ... on Customer {
            ...PackageNeedsAttentionBanner_customer
            ...CustomerSchedule_Bookings
            bookings(
              dateTimeGte: $dateTimeGTE
              first: 100
              orderBy: { field: DATE_TIME, direction: ASC }
            ) {
              count
            }
            managedBookings(
              dateTimeGte: $dateTimeGTE
              first: 100
              orderBy: { field: DATE_TIME, direction: ASC }
            ) {
              count
            }
            firstName
          }
          ... on Member {
            ...MemberSchedule_Bookings
            ...MemberSchedule_EventSignUps
            bookings(
              dateTimeGte: $dateTimeGTE
              first: 100

              orderBy: { field: DATE_TIME, direction: ASC }
            ) {
              count
            }
            managedBookings(
              dateTimeGte: $dateTimeGTE
              first: 100
              orderBy: { field: DATE_TIME, direction: ASC }
            ) {
              count
            }

            eventSignUps(dateGte: $dateGTE, first: 100) {
              count
            }
            firstName
          }
        }
      }
    }
  }
`;
